import React, { useContext, useEffect, useState } from 'react';
import { ServiceResponse } from '../../../app/services/shared/interfaces';
import { toast } from 'react-toastify';
import { AppContext } from '../../../contexts/AppContext';
import useSalesOrderService from '../../../app/services/hooks/useSalesOrderService';
import useNavigationPage from '../../../hooks/useNavigationPage';
import useReactConfirmAlert from '../../../hooks/useReactConfirmAlert';
import { defaultSalesOrderDataForm, SalesOrderDataForm } from '../../../app/models/SalesOrder';
import useFetch from '../../../hooks/useFetch';
import { EndPoints } from '../../../app/services/EndPoints';
import { Society } from '../../../app/models/Society';
import { SocietyClientDataForm } from '../../../app/models/SocietyClient';
import { Currency } from '../../../app/models/Currency';
import { PaymentMethod } from '../../../app/models/PaymentMethod';
import { PaymentCondition } from '../../../app/models/PaymentCondition';
import { Sector } from '../../../app/models/Sector';
import { Incoterm } from '../../../app/models/Incoterm';
import { Location } from '../../../app/models/Location';

import SalesOrderForm from './forms/SalesOrderForm';
import ButtonCancelForm from '../../../components/buttons/ButtonCancelForm';
import ButtonSaveForm from '../../../components/buttons/ButtonSaveForm';
import LazyLoading from '../../../components/LazyLoading';

interface Props {
    onSaved?: () => void;
    onCancel?: () => void;
    onError?: () => void;
}

const SalesOrderCreate = ({ onSaved, onCancel, onError }: Props) => {
    const { showLoading, hideLoading } = useContext(AppContext);
    const { navigationPage } = useNavigationPage();
    const { doGet } = useFetch();

    const { storeSalesOrder, fetchingStoreSalesOrder } = useSalesOrderService();
    const { getClientsBySociety } = useSalesOrderService();

    const [salesOrder, setSalesOrder] = useState<SalesOrderDataForm>(defaultSalesOrderDataForm);
    const [errorFields, setErrorFields] = useState<any>();
    const [societies, setSocieties] = useState<Society[]>([]);
    const [societyClients, setSocietyClients] = useState<SocietyClientDataForm[]>([]);
    const [clientContacts, setClientContacts] = useState<any[]>([]);
    const [sectors, setSectors] = useState<Sector[]>([]);
    const [currencies, setCurrencies] = useState<Currency[]>([]);
    const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
    const [paymentConditions, setPaymentConditions] = useState<PaymentCondition[]>([]);
    const [locations, setLocations] = useState<Location[]>([]);
    const [incoterms, setIncoterms] = useState<Incoterm[]>([]);
    const [loadingData, setLoadingData] = useState(true);

    useEffect(() => {
        loadInitialData();
    }, []);

    // Cargar clientes cuando se seleccione una sociedad
    useEffect(() => {
        if (salesOrder.society_id) {
            getClientsBySociety(salesOrder.society_id, {
                onSuccess: (response: ServiceResponse) => {
                    const clientsData = response.data.society_clients || [];
                    setSocietyClients(clientsData);
                    // Resetear cliente si cambia la sociedad
                    if (salesOrder.client_id) {
                        setSalesOrder({ ...salesOrder, client_id: undefined, contact_name: '', contact_email: '' });
                    }
                },
                onError: (response: ServiceResponse) => {
                    toast.error(response.message);
                    setSocietyClients([]);
                }
            });
        } else {
            setSocietyClients([]);
        }
    }, [salesOrder.society_id]);

    // Cargar contactos cuando se seleccione un cliente
    useEffect(() => {
        if (salesOrder.client_id) {
            doGet({
                url: EndPoints.BUSINESS_NAME_CONTACTS.GET_BUSINESS_NAME_CONTACT_BY_CLIENT.replace(':client_id', salesOrder.client_id.toString()),
                onSuccess: (response: ServiceResponse) => {
                    const contactsData = response.data.client_contacts || [];
                    setClientContacts(contactsData);
                },
                onError: (response: ServiceResponse) => {
                    toast.error(response.message);
                    setClientContacts([]);
                }
            });
        } else {
            setClientContacts([]);
        }
    }, [salesOrder.client_id]);

    const loadInitialData = () => {
        if (showLoading) showLoading('loading', 'Cargando datos...');

        // Cargar todos los datos iniciales desde el endpoint create
        doGet({
            url: EndPoints.SALES_ORDERS.CREATE_SALES_ORDER,
            onSuccess: (response: ServiceResponse) => {
                setSocieties(response.data.societies || []);
                setSectors(response.data.sectors || []);
                setIncoterms(response.data.incoterms || []);
                setLocations(response.data.locations || []);
                setCurrencies(response.data.currencies || []);
                setPaymentMethods(response.data.payment_methods || []);
                setPaymentConditions(response.data.payment_conditions || []);
                setLoadingData(false);
                if (hideLoading) hideLoading();
            },
            onError: (errorResponse: ServiceResponse) => {
                console.error('Error loading initial data:', errorResponse);
                toast.error(errorResponse.message || 'Error al cargar los datos iniciales');
                setLoadingData(false);
                if (hideLoading) hideLoading();
            }
        });
    };

    const store = () => {
        if (showLoading) showLoading('loading', 'Guardando orden de venta...');

        storeSalesOrder(salesOrder, {
            onSuccess: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();

                useReactConfirmAlert().successAlert({
                    title: 'Éxito',
                    message: response.message
                });

                toast.success(response.message, { autoClose: 2500 });

                // Navegar a edición con el ID creado
                navigationPage('/sales-orders/' + response.data.sales_order_id + '/edit');

                if (onSaved) onSaved();
            },
            onError: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();

                useReactConfirmAlert().errorAlert({
                    title: 'Error',
                    message: response.message
                });
            },
            onFieldError: (errorFields: ServiceResponse) => {
                if (hideLoading) hideLoading();
                setErrorFields(errorFields.data);
            }
        });
    };

    const cancel = () => {
        if (onCancel) onCancel();
    };

    if (loadingData) {
        return <LazyLoading height={300} />;
    }

    return (
        <>
            <div className="row">
                <div className="col-12">
                    <SalesOrderForm
                        salesOrder={salesOrder}
                        setSalesOrder={setSalesOrder}
                        societies={societies}
                        societyClients={societyClients}
                        clientContacts={clientContacts}
                        sectors={sectors}
                        currencies={currencies}
                        paymentMethods={paymentMethods}
                        paymentConditions={paymentConditions}
                        locations={locations}
                        incoterms={incoterms}
                        errorFields={errorFields}
                    />
                </div>
            </div>

            <hr />

            <div className="row justify-content-end">
                <div className="col-auto">
                    <ButtonCancelForm callbackCancel={cancel} locked={fetchingStoreSalesOrder} />
                </div>
                <div className="col-auto">
                    <ButtonSaveForm callbackSave={store} locked={fetchingStoreSalesOrder} />
                </div>
            </div>
        </>
    );
};

export default SalesOrderCreate;
